import { PageHeader } from '@ant-design/pro-components';
import { Button, Divider, Space, Typography, message } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useAsyncFn } from 'react-use';
import React from 'react';
import { AppSettingResponseModel } from '@uniquegood/realworld-admin-interface/dist';
import { PlusCircleOutlined } from '@ant-design/icons';
import DefaultLayout from '@src/components/DefaultLayout';
import { miscApi } from '@src/apis/admin';
import useModalState from '@src/hooks/useModalState';
import CreateSettingModal from './CreateSettingModal';
import { SettingItem } from './SettingItem';

const { Title } = Typography;

export default function SystemSettings() {
  const [settingData, setSettingData] = React.useState<AppSettingResponseModel>();
  const [os, setOs] = React.useState<'ios' | 'android' | 'common'>();

  const initialSettingData = React.useRef<AppSettingResponseModel>(null);
  const hasDifference = JSON.stringify(initialSettingData.current) !== JSON.stringify(settingData);

  const { openModal, closeModal, modal } = useModalState();

  const [settingDataApi, fetchSettingDataApi] = useAsyncFn(async () => {
    const { data } = await miscApi.getAppSetting_1('mobileAppSetting');
    initialSettingData.current = data.data;
    setSettingData(data.data);

    return data;
  }, []);

  /** 세팅 데이터 업데이트를 위한 BODY 형식 변환 */
  const toUpsertPayload = (data: AppSettingResponseModel) => {
    const { key, isJson, isPrivate, content } = data;
    const contentString =
      typeof content === 'string' ? content : JSON.stringify(content);

    return {
      key,
      content: contentString,
      isJson: isJson ?? true,
      isPrivate: isPrivate ?? false
    };
  }

  /** 세팅 데이터 UI, 내부 데이터 업데이트 */
  const handleSettingData = (data: AppSettingResponseModel) => {
    setSettingData(data);
  };

  const handleClick = (osValue: typeof os) => {
    setOs(osValue);
    openModal({
      title: '설정 추가',
      transitionName: ''
    });
  };

  /** 세팅 데이터 저장 */
  const handleSaveClick = async () => {
    try {
      if (settingData) {
        console.log(settingData);
        await miscApi.createAppSetting(undefined,toUpsertPayload(settingData!));
        message.success('저장에 성공했습니다.');
      }

      fetchSettingDataApi();
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    fetchSettingDataApi();
  }, [fetchSettingDataApi]);

  return (
    <>
      <DefaultLayout>
        <PageHeader
          title="시스템 관리"
          subTitle="시스템 관련 설정 할 수 있습니다."
          style={{ margin: '16px 20px 0px 20px' }}
        />
        <Divider />

        <Content style={{ padding: '0 24px', margin: '0px 20px' }}>
          <div style={{ width: '100%', textAlign: 'right' }}>
            <Button type="primary" disabled={!hasDifference} onClick={handleSaveClick}>
              저장
            </Button>
          </div>

          /** IOS 세팅 데이터 */
          <Space>
            <Title level={3} style={{ margin: 0 }}>
              IOS
            </Title>
            <Button type="dashed" shape="circle" icon={<PlusCircleOutlined />} onClick={() => handleClick('ios')} />
          </Space>
          {!settingDataApi.loading && settingData?.content?.ios && (
            <div style={{ marginBottom: '16px' }}>
              {Object.entries(settingData?.content.ios).map((item) => (
                <SettingItem
                  key={item[0]}
                  item={item}
                  osType="ios"
                  settingData={settingData}
                  setSettingData={setSettingData}
                />
              ))}
            </div>
          )}
          /** Android 세팅 데이터 */
          <Space>
            <Title level={3} style={{ margin: 0 }}>
              Android
            </Title>
            <Button type="dashed" shape="circle" icon={<PlusCircleOutlined />} onClick={() => handleClick('android')} />
          </Space>
          {!settingDataApi.loading && settingData?.content?.android && (
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
              {Object.entries(settingData?.content.android).map((item) => (
                <SettingItem
                  key={item[0]}
                  item={item}
                  osType="android"
                  settingData={settingData}
                  setSettingData={setSettingData}
                />
              ))}
            </div>
          )}
          /** Common 세팅 데이터 */
          <Space>
            <Title level={3} style={{ margin: 0 }}>
              Common
            </Title>
            <Button type="dashed" shape="circle" icon={<PlusCircleOutlined />} onClick={() => handleClick('common')} />
          </Space>

          {!settingDataApi.loading && settingData?.content?.common && (
            <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
              {Object.entries(settingData?.content.common).map((item) => (
                <SettingItem
                  key={item[0]}
                  item={item}
                  osType="common"
                  settingData={settingData}
                  setSettingData={setSettingData}
                />
              ))}
            </div>
          )}
        </Content>
      </DefaultLayout>
      /** 세팅 데이터 추가 모달 */
      <CreateSettingModal
        modal={modal}
        closeModal={closeModal}
        currentData={settingData || ({} as AppSettingResponseModel)}
        setSettingData={handleSettingData}
        os={os}
      />
    </>
  );
}
